<template>
  <div class="login-verification">
    <h5 class="title">{{ sentCode ? 'Código enviado' : 'Código de verificación' }}</h5>
    <p v-if="!sentCode" class="description">Para acceder a tu cuenta Joinnus y descubrir la ciudad, necesitas solicitar un código de verificación.</p>
    <p v-if="sentCode" class="description">
      <template v-if="selectedMethod === 'email'">
        Hemos enviado el código al correo que tienes
        registrado en Joinnus: <b>{{ secureEmail(user.email) }}</b>.
        Revisa tu bandeja de entrada o spam e ingrésalo
        para acceder a tu cuenta.
      </template>
      <template v-else-if="selectedMethod === 'alt-email'">
        ¡Listo! Hemos enviado el código de verificación
        a tu correo alterno registrado en Joinnus {{ secureEmail(user.contactEmail) }}.
        En cuanto lo recibas, introdúcelo aquí para acceder a tu cuenta.
      </template>
      <template v-else-if="selectedMethod === 'phone'">
        ¡Listo! Hemos enviado el código de verificación a tu celular por
        {{ phoneVerificationMode === 'sms' ? 'SMS' : 'Whatsapp' }}
        al {{ user.phoneCode }} {{ securePhone(user.phone) }}. En cuanto lo recibas,
        introdúcelo aquí para acceder a tu cuenta.
      </template>
    </p>
    <p v-if="justEmail && !sentCode" class="description">Te enviaremos un código al correo registrado en Joinnus {{ secureEmail(user.email) }}</p>
    <div v-if="sentCode">
      <p class="subtitle">Código de verificación:</p>
      <div style="margin: '16px 0'">
        <verification-code
          :code="verificationCode"
          @change="verificationCode = $event; verificationError = ''">
        </verification-code>
      </div>
      <resend-code
        :resend="resendCode"
      ></resend-code>
    </div>
    <div v-else-if="!selectingMethod && selectedMethod === 'phone'">
      <p class="description">Te enviaremos un código al correo registrado en Joinnus {{ user.phoneCode }} {{ securePhone(user.phone) }}</p>
      <p class="description">¿Por dónde quieres que te enviemos el código?</p>
      <div class="phone-methods">
        <div class="phone-method">
          <input
            :checked="phoneVerificationMode === 'whatsapp'"
            @change="phoneVerificationMode = 'whatsapp'"
            type="radio"
            id="codeByWsp"
            name="code"
          />
          <label for="codeByWsp" class="m-0 pl-2 font-medium">Whatsapp</label>
        </div>
        <!-- <div class="phone-method">
          <input
            :checked="phoneVerificationMode === 'sms'"
            @change="phoneVerificationMode = 'sms'"
            type="radio"
            id="codeBySMS"
            name="code"
          />
          <label for="codeBySMS" class="m-0 font-medium pl-2">SMS</label>
        </div> -->
      </div>
      <p class="help">
        ¿Este no es tu número? No te preocupes.
        Si no es tuyo o no puedes acceder,
        pide que lo actualicemos en
        <a href="mailto:info@joinnus.com">Ayuda y contacto</a>.
      </p>
    </div>
    <p v-else-if="!selectingMethod && selectedMethod === 'alt-email'" class="description">
      Te enviaremos un código al correo alterno que registraste
      en Joinnus {{ secureEmail(user.contactEmail) }}
    </p>
    <p v-else-if="!selectingMethod && selectedMethod === 'email'" class="description">
      Te enviaremos un código al correo registrado
      en Joinnus {{ secureEmail(user.email) }}
    </p>
    <div v-else-if="!justEmail" class="methods">
      <label
        for="two-step-email"
        class="method"
      >
        <input
          :checked="selectedMethod === 'email'"
          @change="selectedMethod = 'email'"
          type="radio"
          id="two-step-email"
          name="methods-radio"
        />
        <div>
          <span class="method-label">Correo principal</span>
          <div class="method-value">
            <simple-mail-icon></simple-mail-icon>
            <p class="value-text">{{ secureEmail(user.email) }}</p>
          </div>
        </div>
      </label>
      <label
        v-if="withPhone"
        for="two-step-phone"
        class="method"
      >
        <input
          :checked="selectedMethod === 'phone'"
          @change="selectedMethod = 'phone'"
          type="radio"
          id="two-step-phone"
          name="methods-radio"
        />
        <div>
          <span class="method-label">Número celular</span>
          <div class="method-value">
            <call-icon></call-icon>
            <p class="value-text">{{ user.phoneCode }} {{ securePhone(user.phone) }}</p>
          </div>
        </div>
      </label>
      <label
        v-if="withAltEmail"
        for="two-step-alt-email"
        class="method"
      >
        <input
          :checked="selectedMethod === 'alt-email'"
          @change="selectedMethod = 'alt-email'"
          type="radio"
          id="two-step-alt-email"
          name="methods-radio"
        />
        <div>
          <span class="method-label">Correo alterno</span>
          <div class="method-value">
            <simple-mail-icon></simple-mail-icon>
            <p class="value-text">{{ secureEmail(user.contactEmail) }}</p>
          </div>
        </div>
      </label>
    </div>
    <p
      v-if="!sentCode && (justEmail || (!selectingMethod && ['email', 'alt-email'].includes(selectedMethod)))"
      class="help"
    >
      ¿Este no es tu correo? No te preocupes.
      Si no es tuyo o no puedes acceder,
      pide que lo actualicemos en <a href="mailto:info@joinnus.com">Ayuda y contacto</a>.
    </p>
    <p v-if="verificationError" class="error-msg">{{ verificationError }}</p>
    <div class="actions">
      <button
        type="button"
        class="go-back-btn"
        @click="goBack()"
      >
        Volver
      </button>
      <the-button
        class="w-full"
        :loading="btnLoading"
        :disabled="sentCode ? verificationCode.length !== 6 : false"
        @click="onContinue"
      >
        {{ continueBtnText }}
      </the-button>
    </div>
  </div>
</template>
<script>
import { securePhone, secureEmail, getServerError } from "../../utils/helpers";
import api from "../../api";
import TheButton from './../auth/TheButton.vue';
import CallIcon from './../icons/CallIcon.vue';
import SimpleMailIcon from './../icons/SimpleMailIcon.vue';
import VerificationCode from './../auth/VerificationCode.vue';
import ResendCode from './../auth/ResendCode.vue';

export default {
  components: { TheButton, CallIcon, SimpleMailIcon, VerificationCode, ResendCode },
  props: {
    user: {
      type: Object,
      required: true,
    },
    userPriv: {
      type: Object,
      required: true,
    }
  },
  data() {
    const { user } = this;
    // user.twoStepVerify.isEmailVerified = false;
    // user.twoStepVerify.isPhoneVerified = false;
    // user.twoStepVerify.isEmailAlternativeVerified = false;
    const withEmail = user.email && user.twoStepVerify.isEmailVerified;
    const withPhone = withEmail && user.phone && user.twoStepVerify.isPhoneVerified;
    const withAltEmail = withEmail && user.contactEmail && user.twoStepVerify.isEmailAlternativeVerified && withPhone;
    const justEmail = !withPhone && !withAltEmail;
    return {
      withPhone,
      withAltEmail,
      justEmail,
      inPhoneFlow: false,
      inAltEmailFlow: false,
      selectedMethod: 'email',
      sentCode: false,
      verificationCode: '',
      phoneVerificationMode: 'whatsapp',
      selectingMethod: true,
      verificationError: '',
      btnLoading: false,
    };
  },
  computed: {
    continueBtnText () {
      let text = 'Continuar';
      if (this.sentCode) text = 'Validar código';
      else if (this.justEmail || !this.selectingMethod) text = 'Solicitar código';
      return text;
    }
  },
  watch: {},
  methods: {
    secureEmail,
    securePhone,
    errorHandler(error) {
      console.error(error);
      this.verificationError = getServerError(error);
    },
    clearError() {
      this.verificationError = '';
    },
    async onContinue() {
      this.clearError();
      const {
        justEmail,
        selectingMethod,
        selectedMethod,
        phoneVerificationMode,
        sentCode,
        verificationCode,
        user,
      } = this;
      this.btnLoading = true;
      if (sentCode) {
        try {
          const verificationRes = await api.validateLoginCode({
            type: selectedMethod,
            code: verificationCode,
            user,
            token: user.token
          });
          this.$emit('success', { session: verificationRes.session });
        } catch (error) {
          console.log('error', error);
          this.errorHandler(error);
        }
      } else if (justEmail) {
        try {
          await api.sendLoginCode({ type: 'email', user });
          this.sentCode = true;
        } catch (error) {
          this.errorHandler(error);
        }
      } else if (selectingMethod) {
        this.selectingMethod = false;
      } else if (selectedMethod) {
        const type = selectedMethod === 'phone' ? phoneVerificationMode : selectedMethod;
        try {
          await api.sendLoginCode({ type, user });
          this.sentCode = true;
        } catch (error) {
          this.errorHandler(error);
        }
      }
      this.btnLoading = false;
    },
    goBack() {
      this.clearError();
      const { sentCode, selectingMethod } = this;
      if (sentCode) {
        this.sentCode = false;
      } else if (selectingMethod) {
        this.$emit('cancel')
      } else {
        this.selectingMethod = true;
      }
    },
    async resendCode() {
      const {
        selectedMethod,
        phoneVerificationMode,
        user
      } = this;
      const token = this.session;
      const type = selectedMethod === 'phone' ? phoneVerificationMode : selectedMethod;
      try {
        await api.sendLoginCode({ type, user, token });
        this.verificationCode = '';
        this.verificationError = '';
      } catch (error) {
        this.errorHandler(error);
      }
    },
  }
};
</script>
<style lang="scss">
.login-verification {
  max-width: 556px;
  margin: 0 auto;
  background: white;
  border-radius: 1rem;
  padding: 1.5rem;
  .title {
    color: #031425;
    font-weight: 800;
    font-size: 34px;
    margin-bottom: 32px;
    text-align: center;
  }
  .description {
    font-weight: 500;
    font-size: 16px;
    b {
      font-weight: 700;
    }
  }
  .error-msg {
    color: #BA1A1A;
    font-size: 12px;
    font-weight: 500;
    margin: 0;
    margin-top: 10px;
  }
  .subtitle {
    font-size: 14px;
    font-weight: 500;
  }
  .help {
    font-size: 12px;
    color: #354351;
    font-weight: 500;
    a {
      color: #00B38E;
      font-weight: 700;
    }
  }
  .actions {
    display: flex;
    justify-content: space-between;
    gap: 28px;
    margin-top: 40px;
    @media (max-width: 480px) {
      flex-direction: column-reverse;
    }
    .go-back-btn {
      border: 2px solid #031425;
      padding: 12px 16px 12px 16px;
      text-align: center;
      width: 100%;
      font-weight: 700;
      background: none;
      border-radius: 24px;
    }
  }
  .phone-methods {
    display: flex;
    margin: 32px 0;
    .phone-method {
      display: flex;
      padding-left: 1.25rem;
      padding-right: 1.25rem;
      width: 100%;
      font-weight: 500;
    }
  }
  .methods {
    display: flex;
    gap: 8px;
    flex-direction: column;
    margin-top: 24px;
    .method {
      display: flex; 
      padding-top: 0.5rem;
      padding-bottom: 0.5rem; 
      padding-left: 1.5rem;
      padding-right: 1.5rem; 
      margin: 0; 
      gap: 1rem; 
      align-items: center; 
      border-radius: 0.25rem; 
      border: 1px solid #E4EDF5; 
      .method-label {
        display: block; 
        margin-bottom: 0.5rem;
        font-weight: 400; 
        font-size: 10px;
        color: #031425;
      }
      .method-value {
        display: flex;
        align-items: center;
        gap: 10px;
        .value-text {
          margin: 0;
          font-weight: 500;
        }
      }
    }
  }
}

.w-full {
  width: 100%;
}
</style>